import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Permission, PermissionStrategy } from './permission.types';
import { StorageService } from 'app/core/auth/storage.service';

@Injectable({
    providedIn: 'root',
})
export class PermissionService {
    private _permissions = new BehaviorSubject<Set<string>>(new Set<string>());
    public permissions$: Observable<string[]> = this._permissions.asObservable().pipe(
        map((set) => Array.from(set))
    );

    private _isLoaded = new BehaviorSubject<boolean>(false);
    public isLoaded$: Observable<boolean> = this._isLoaded.asObservable();

    constructor(private _storageService: StorageService) {
        // Otomatis load dari storage jika ada session existing saat inisialisasi awal
        this.loadFromStorage();
    }

    /**
     * Memeriksa apakah user memiliki permission tertentu (single check).
     * Mendukung exact match, global wildcard '*', dan module wildcard '[module].*'.
     *
     * @param permission Nama permission (e.g. 'master.read', 'product.create')
     * @returns true jika diizinkan, false jika tidak
     */
    hasPermission(permission: string | null | undefined): boolean {
        if (!permission || typeof permission !== 'string' || !permission.trim()) {
            return false;
        }

        const currentPermissions = this._permissions.value;
        if (currentPermissions.size === 0) {
            return false;
        }

        // Global wildcard bypass (super admin)
        if (currentPermissions.has('*')) {
            return true;
        }

        const normalized = permission.trim().toLowerCase();

        // Exact match
        if (currentPermissions.has(normalized)) {
            return true;
        }

        // Module-level wildcard check (e.g. 'user.*' matches 'user.read')
        const dotIndex = normalized.lastIndexOf('.');
        if (dotIndex > 0) {
            const moduleWildcard = `${normalized.substring(0, dotIndex)}.*`;
            if (currentPermissions.has(moduleWildcard)) {
                return true;
            }
        }

        // Smart singular/plural module alias check (e.g. 'users.read' <-> 'user.read', 'companies.read' <-> 'company.read')
        const parts = normalized.split('.');
        if (parts.length === 2) {
            const [mod, act] = parts;
            const alternateMods: string[] = [];

            if (mod.endsWith('ies')) {
                alternateMods.push(mod.slice(0, -3) + 'y'); // companies -> company
            } else if (mod.endsWith('y')) {
                alternateMods.push(mod.slice(0, -1) + 'ies'); // company -> companies
            }

            if (mod.endsWith('s')) {
                alternateMods.push(mod.slice(0, -1)); // divisions -> division, users -> user
            } else {
                alternateMods.push(`${mod}s`); // division -> divisions, user -> users
            }

            for (const altMod of alternateMods) {
                const alternatePerm = `${altMod}.${act}`;
                const alternateWildcard = `${altMod}.*`;

                if (currentPermissions.has(alternatePerm) || currentPermissions.has(alternateWildcard)) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * Memeriksa apakah user memiliki MINIMAL SATU dari daftar permission yang diberikan (OR logic).
     *
     * @param permissions Daftar permission
     * @returns true jika user memiliki minimal satu permission (atau jika array kosong/tidak ada syarat)
     */
    hasAnyPermission(permissions: string[] | null | undefined): boolean {
        if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
            return true;
        }
        return permissions.some((perm) => this.hasPermission(perm));
    }

    /**
     * Memeriksa apakah user memiliki SEMUA dari daftar permission yang diberikan (AND logic).
     *
     * @param permissions Daftar permission
     * @returns true jika user memiliki seluruh permission (atau jika array kosong/tidak ada syarat)
     */
    hasAllPermissions(permissions: string[] | null | undefined): boolean {
        if (!permissions || !Array.isArray(permissions) || permissions.length === 0) {
            return true;
        }
        return permissions.every((perm) => this.hasPermission(perm));
    }

    /**
     * Helper serbaguna untuk mengecek single atau multiple permission dengan strategi tertentu.
     *
     * @param permissionOrPermissions string atau string[] permission
     * @param strategy 'ANY' (default) atau 'ALL'
     */
    hasAccess(
        permissionOrPermissions: string | string[] | null | undefined,
        strategy: PermissionStrategy = 'ANY'
    ): boolean {
        if (!permissionOrPermissions) {
            return true;
        }

        if (typeof permissionOrPermissions === 'string') {
            return this.hasPermission(permissionOrPermissions);
        }

        if (Array.isArray(permissionOrPermissions)) {
            if (permissionOrPermissions.length === 0) {
                return true;
            }
            return strategy === 'ALL'
                ? this.hasAllPermissions(permissionOrPermissions)
                : this.hasAnyPermission(permissionOrPermissions);
        }

        return false;
    }

    /**
     * Mengatur daftar permission secara langsung.
     * Menerima array of strings maupun array of objects dengan key 'name' / 'code' / 'slug'.
     *
     * @param permissions Daftar permissions mentah dari backend
     */
    setPermissions(permissions: (string | any)[] | null | undefined): void {
        const normalizedSet = new Set<string>();

        if (permissions && Array.isArray(permissions)) {
            for (const item of permissions) {
                const extracted = this._extractPermissionString(item);
                if (extracted) {
                    normalizedSet.add(extracted.toLowerCase());
                }
            }
        }

        this._permissions.next(normalizedSet);
        this._isLoaded.next(true);
    }

    /**
     * Mengambil snapshot array dari seluruh permission aktif saat ini.
     */
    getPermissions(): string[] {
        return Array.from(this._permissions.value);
    }

    /**
     * Status apakah permission sudah selesai dimuat.
     */
    get isLoaded(): boolean {
        return this._isLoaded.value;
    }

    /**
     * Memuat permission langsung dari objek user backend.
     * Mendukung berbagai format struktur response backend:
     * - user.permissions: string[] / object[]
     * - user.roles: [{ permissions: [...] }]
     * - user.role: 'admin' (jika role memiliki wildcard '*')
     *
     * @param user Objek user terautentikasi
     */
    loadFromUser(user: any): void {
        if (!user) {
            this.clear();
            return;
        }

        const rawList: (string | any)[] = [];

        // 1. Direct permissions array pada user (user.permissions: string[] / object[])
        if (Array.isArray(user.permissions)) {
            rawList.push(...user.permissions);
        }

        // 2. Jika user.role adalah Object: { id: 1, name: "Super Admin", permissions: [...] }
        if (typeof user.role === 'object' && user.role !== null) {
            if (Array.isArray(user.role.permissions)) {
                rawList.push(...user.role.permissions);
            }
            const roleName = (user.role.name || '').toLowerCase().replace(/[\s_-]/g, '');
            if (roleName === 'superadmin' || user.role.id === 1 || user.role.name === '*') {
                rawList.push('*');
            }
        } else if (typeof user.role === 'string') {
            const r = user.role.toLowerCase().replace(/[\s_-]/g, '');
            if (r === 'superadmin' || user.role === '*') {
                rawList.push('*');
            }
        }

        // 3. Jika user.roles adalah Array: [{ permissions: [...] }]
        if (Array.isArray(user.roles)) {
            for (const role of user.roles) {
                if (typeof role === 'object' && role !== null) {
                    if (Array.isArray(role.permissions)) {
                        rawList.push(...role.permissions);
                    }
                    const roleName = (role.name || '').toLowerCase().replace(/[\s_-]/g, '');
                    if (roleName === 'superadmin' || role.id === 1 || role.name === '*') {
                        rawList.push('*');
                    }
                } else if (typeof role === 'string') {
                    const r = role.toLowerCase().replace(/[\s_-]/g, '');
                    if (r === 'superadmin' || role === '*') {
                        rawList.push('*');
                    }
                }
            }
        }

        // 4. Fallback jika username adalah superadmin
        if (typeof user.username === 'string' && user.username.toLowerCase() === 'superadmin') {
            rawList.push('*');
        }

        this.setPermissions(rawList);
    }

    /**
     * Memuat data permission dari localStorage (mendukung session terenkripsi maupun dev_session).
     */
    loadFromStorage(): void {
        const userStr = localStorage.getItem('user');
        if (!userStr) {
            return;
        }

        const isDevSession = localStorage.getItem('dev_session') === 'true';

        try {
            let userData: any = null;
            if (isDevSession) {
                userData = JSON.parse(userStr);
            } else {
                try {
                    userData = JSON.parse(userStr);
                } catch {
                    try {
                        const decrypted = this._storageService.decryptionAES(userStr);
                        userData = JSON.parse(decrypted);
                    } catch (decErr) {
                        console.warn('[PermissionService] Failed to decrypt user from storage', decErr);
                        return;
                    }
                }
            }

            if (userData) {
                this.loadFromUser(userData);
            }
        } catch (e) {
            console.warn('[PermissionService] Failed to load permissions from storage', e);
        }
    }

    /**
     * Membersihkan permission saat logout.
     * Mencegah permission user sebelumnya terbawa ke user berikutnya.
     */
    clear(): void {
        this._permissions.next(new Set<string>());
        this._isLoaded.next(false);
    }

    /**
     * Ekstraksi string permission dari berbagai format input
     */
    private _extractPermissionString(item: any): string | null {
        if (!item) return null;
        if (typeof item === 'string') return item.trim();
        if (typeof item === 'object') {
            const val = item.name || item.code || item.slug || item.permission || item.action;
            if (typeof val === 'string') return val.trim();
        }
        return null;
    }
}
